import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon } from 'lucide-react';
export function QuickAssessmentPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  // State for all answers
  const [deviceCount, setDeviceCount] = useState<number>(5);
  const [biggestWorry, setBiggestWorry] = useState<string | null>(null);
  const [reusedPassword, setReusedPassword] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<string | null>(null);
  const [whoElse, setWhoElse] = useState<string[]>([]);
  const totalSteps = 5;
  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      navigate('/first-look');
    }
  };
  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };
  const handleSkip = () => {
    navigate('/first-look');
  };
  const toggleWhoElse = (option: string) => {
    if (option === 'Just me') {
      setWhoElse(['Just me']);
      return;
    }
    let newSelection = [...whoElse].filter(item => item !== 'Just me');
    if (newSelection.includes(option)) {
      newSelection = newSelection.filter(item => item !== option);
    } else {
      newSelection.push(option);
    }
    setWhoElse(newSelection);
  };
  const isContinueDisabled = () => {
    switch (step) {
      case 1:
        return false;
      // Slider always has a value
      case 2:
        return biggestWorry === null;
      case 3:
        return reusedPassword === null;
      case 4:
        return lastUpdate === null;
      case 5:
        return whoElse.length === 0;
      default:
        return true;
    }
  };
  // Animation variants
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 50 : -50,
      opacity: 0
    })
  };
  // We use a fixed direction for simplicity in this demo
  const direction = 1;
  return <div className="min-h-screen bg-[#f8f8f8] flex flex-col font-sans text-[#111111]" data-id="element-4721">
      {/* Top Bar */}
      <div className="w-full max-w-md mx-auto px-5 pt-6 pb-4 flex items-center justify-between z-10 relative" data-id="element-4722">
        <div className="w-10" data-id="element-4723">
          {step > 1 && <button onClick={handleBack} className="p-2 -ml-2 rounded-full hover:bg-gray-200 transition-colors text-gray-600 border-0 bg-transparent cursor-pointer" data-id="element-4724">
              <ArrowLeftIcon size={20} data-id="element-4725" />
            </button>}
        </div>
        <div className="text-sm font-medium text-gray-500" data-id="element-4726">
          Step {step} of {totalSteps}
        </div>
        <div className="w-10 text-right" data-id="element-4727">
          <button onClick={handleSkip} className="text-sm font-medium text-gray-400 hover:text-gray-600 border-0 bg-transparent cursor-pointer p-0" data-id="element-4728">
            Skip
          </button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="w-full max-w-md mx-auto px-5 mb-8" data-id="element-4729">
        <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden" data-id="element-4730">
          <motion.div className="h-full bg-[#01ced1]" initial={{
          width: `${(step - 1) / totalSteps * 100}%`
        }} animate={{
          width: `${step / totalSteps * 100}%`
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} data-id="element-4731" />
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 w-full max-w-md mx-auto px-5 relative flex flex-col" data-id="element-4732">
        <AnimatePresence mode="wait" custom={direction} data-id="element-4733">
          <motion.div key={step} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          x: {
            type: 'spring',
            stiffness: 300,
            damping: 30
          },
          opacity: {
            duration: 0.2
          }
        }} className="flex-1 flex flex-col" data-id="element-4734">
            {step === 1 && <div className="flex flex-col flex-1" data-id="element-4735">
                <h2 className="text-2xl md:text-3xl font-bold text-[#1F0648] mb-12 leading-tight" data-id="element-4736">
                  How many devices does your household use (phones, tablets,
                  computers, smart TVs)?
                </h2>
                <div className="flex flex-col items-center justify-center flex-1 pb-20" data-id="element-4737">
                  <div className="text-7xl font-extrabold text-[#8528e2] mb-8" data-id="element-4738">
                    {deviceCount}
                    {deviceCount === 10 ? '+' : ''}
                  </div>
                  <input type="range" min="1" max="10" value={deviceCount} onChange={e => setDeviceCount(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#8528e2]" style={{
                accentColor: '#8528e2'
              }} data-id="element-4739" />
                  <div className="w-full flex justify-between text-xs text-gray-400 font-medium mt-3 px-1" data-id="element-4740">
                    <span data-id="element-4741">1</span>
                    <span data-id="element-4742">10+</span>
                  </div>
                </div>
              </div>}

            {step === 2 && <div className="flex flex-col flex-1" data-id="element-4743">
                <h2 className="text-2xl md:text-3xl font-bold text-[#1F0648] mb-8 leading-tight" data-id="element-4744">
                  Which best describes your biggest worry right now?
                </h2>
                <div className="space-y-3" data-id="element-4745">
                  {['Getting scammed or phished', 'Protecting my accounts', 'Keeping my kids safe online', 'My parents getting targeted'].map(option => <button key={option} onClick={() => setBiggestWorry(option)} className={`w-full text-left p-5 rounded-2xl border-2 transition-all duration-200 cursor-pointer ${biggestWorry === option ? 'border-[#8528e2] bg-[#f5f0ff]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-4746">
                      <span className={`font-bold text-lg ${biggestWorry === option ? 'text-[#8528e2]' : 'text-[#111111]'}`} data-id="element-4747">
                        {option}
                      </span>
                    </button>)}
                </div>
              </div>}

            {step === 3 && <div className="flex flex-col flex-1" data-id="element-4748">
                <h2 className="text-2xl md:text-3xl font-bold text-[#1F0648] mb-10 leading-tight" data-id="element-4749">
                  Do you use the same password on more than one account?
                </h2>
                <div className="flex flex-col gap-4" data-id="element-4750">
                  {['Yes', 'No', 'Sometimes'].map(option => <button key={option} onClick={() => setReusedPassword(option)} className={`w-full py-4 rounded-full border-2 font-bold text-lg transition-all duration-200 cursor-pointer ${reusedPassword === option ? 'border-[#8528e2] bg-[#8528e2] text-white' : 'border-gray-200 bg-white text-[#111111] hover:border-gray-300'}`} data-id="element-4751">
                      {option}
                    </button>)}
                </div>
              </div>}

            {step === 4 && <div className="flex flex-col flex-1" data-id="element-4752">
                <h2 className="text-2xl md:text-3xl font-bold text-[#1F0648] mb-8 leading-tight" data-id="element-4753">
                  When did you last update your phone's software?
                </h2>
                <div className="space-y-3" data-id="element-4754">
                  {['Recently', 'A few months ago', "I'm not sure", "I don't do updates"].map(option => <button key={option} onClick={() => setLastUpdate(option)} className={`w-full text-left p-5 rounded-2xl border-2 transition-all duration-200 cursor-pointer ${lastUpdate === option ? 'border-[#8528e2] bg-[#f5f0ff]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-4755">
                      <span className={`font-bold text-lg ${lastUpdate === option ? 'text-[#8528e2]' : 'text-[#111111]'}`} data-id="element-4756">
                        {option}
                      </span>
                    </button>)}
                </div>
              </div>}

            {step === 5 && <div className="flex flex-col flex-1" data-id="element-4757">
                <h2 className="text-2xl md:text-3xl font-bold text-[#1F0648] mb-8 leading-tight" data-id="element-4758">
                  Who else in your household needs help with digital safety?
                </h2>
                <p className="text-gray-500 mb-6 -mt-4" data-id="element-4759">
                  Select all that apply
                </p>
                <div className="flex flex-wrap gap-3" data-id="element-4760">
                  {['Spouse or partner', 'Kids under 18', 'Adult children', 'Parents or grandparents', 'Just me'].map(option => {
                const isSelected = whoElse.includes(option);
                return <button key={option} onClick={() => toggleWhoElse(option)} className={`px-5 py-3 rounded-full border-2 font-bold transition-all duration-200 cursor-pointer ${isSelected ? 'border-[#8528e2] bg-[#8528e2] text-white' : 'border-gray-200 bg-white text-[#111111] hover:border-gray-300'}`} data-id="element-4761">
                        {option}
                      </button>;
              })}
                </div>
              </div>}
          </motion.div>
        </AnimatePresence>

        {/* Bottom Continue Button */}
        <div className="mt-auto pt-6 pb-8 bg-[#f8f8f8] z-10 sticky bottom-0" data-id="element-4762">
          <button onClick={handleNext} disabled={isContinueDisabled()} className={`w-full py-4 rounded-full font-bold text-lg transition-all duration-200 ${!isContinueDisabled() ? 'bg-[#8528e2] text-white hover:bg-[#7322c4] shadow-md cursor-pointer' : 'bg-[#8528e2] text-white opacity-50 cursor-not-allowed'}`} data-id="element-4763">
            Continue
          </button>
        </div>
      </div>
    </div>;
}